import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTRACT_EVENT_LABELS, CONTRACT_KIND_LABELS, CONTRACT_PARTY_LABELS, CONTRACT_STATUS_LABELS, TERMINATION_STATUS_LABELS, type Contract, type ContractVerification } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './contracts.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const when = (iso: string | null) => (iso ? stamp.format(new Date(iso)) : '–');

interface SendResult {
  data: Contract;
  link: string;
  mailSent: boolean;
}

/** Ein Vertrag: Stand, Text, Unterschriften und Verlauf, dazu senden, erneut senden, zurückziehen und das PDF. Der Link zur Unterschrift ist nur direkt nach dem Senden zu sehen. */
export function ContractDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can } = useSession();
  const [sending, setSending] = useState(false);
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState<{ link: string; mailSent: boolean } | null>(null);
  const [verification, setVerification] = useState<ContractVerification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [filed, setFiled] = useState<string | null>(null);

  const contract = useQuery({ queryKey: ['contract', id], queryFn: async () => (await api<{ data: Contract }>('GET', `/contracts/${id}`)).data });
  const c0 = contract.data;
  const refresh = (c: Contract) => {
    client.setQueryData(['contract', id], c);
    client.invalidateQueries({ queryKey: ['contracts'] });
  };
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.status === 422 && e.fields.password) setPasswordError(e.fields.password);
    else if (e instanceof ApiError && e.status === 422) setError(Object.values(e.fields)[0] ?? e.message);
    else setError(e instanceof ApiError ? e.message : 'Das hat nicht geklappt.');
  };

  const send = useMutation({
    mutationFn: () => api<SendResult>('POST', `/contracts/${id}/send`, { password }),
    onSuccess: (r) => {
      refresh(r.data);
      setSent({ link: r.link, mailSent: r.mailSent });
      setSending(false);
      setPassword('');
    },
    onError: fail,
  });
  const remind = useMutation({
    mutationFn: () => api<SendResult>('POST', `/contracts/${id}/remind`),
    onSuccess: (r) => {
      refresh(r.data);
      setSent({ link: r.link, mailSent: r.mailSent });
    },
    onError: fail,
  });
  const voidIt = useMutation({
    mutationFn: () => api<{ data: Contract }>('POST', `/contracts/${id}/void`, { reason: reason.trim() || undefined }),
    onSuccess: (r) => {
      refresh(r.data);
      setVoiding(false);
      setSent(null);
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/contracts/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contracts'] });
      navigate('/vertraege', { replace: true });
    },
    onError: fail,
  });
  const fileDocs = useMutation({
    mutationFn: () => api<{ data: { general: boolean; owner: boolean } }>('POST', `/contracts/${id}/file`),
    onSuccess: (r) => {
      client.invalidateQueries({ queryKey: ['contract', id] });
      client.invalidateQueries({ queryKey: ['documents'] });
      setFiled(r.data.general && (r.data.owner || c0?.partyKind === 'other') ? 'Das unterschriebene PDF liegt in Dokumente.' : 'Das PDF konnte nicht überall abgelegt werden. Bitte prüfen Sie Dokumente.');
    },
    onError: fail,
  });
  const verify = useMutation({
    mutationFn: async () => (await api<{ data: ContractVerification }>('GET', `/contracts/${id}/verify`)).data,
    onSuccess: setVerification,
    onError: fail,
  });

  if (contract.isLoading) return <p className="muted">Wird geladen …</p>;
  if (contract.isError || !contract.data) {
    return (
      <div className="card narrow">
        <h1>Vertrag nicht gefunden</h1>
        <Link className="btn btn--secondary" to="/vertraege">Zurück zu den Verträgen</Link>
      </div>
    );
  }
  const c = contract.data;
  const busy = send.isPending || remind.isPending || voidIt.isPending || remove.isPending;

  const submitSend = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPasswordError(null);
    send.mutate();
  };

  return (
    <>
      <p><Link to="/vertraege" className="back-link">← Alle Verträge</Link></p>
      <div className="page-head">
        <h1>{c.title}</h1>
        <span className={`badge badge--contract-${c.status}`}>{CONTRACT_STATUS_LABELS[c.status]}</span>
      </div>
      <p className="muted">{c.number} · {CONTRACT_KIND_LABELS[c.kind]} · {CONTRACT_PARTY_LABELS[c.partyKind]}: {c.partyName} ({c.partyEmail})</p>

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {c.status === 'declined' && <div className="alert alert--warn" role="status">Der Vertragspartner hat den Vertrag abgelehnt.{c.declineReason ? ` Begründung: ${c.declineReason}` : ''}</div>}
      {c.status === 'expired' && <div className="alert alert--warn" role="status">Die Frist zum Unterschreiben ist abgelaufen. Legen Sie den Vertrag neu an, wenn er noch gelten soll.</div>}
      {c.status === 'void' && <div className="alert alert--warn" role="status">Der Vertrag ist zurückgezogen.{c.declineReason ? ` Grund: ${c.declineReason}` : ''}</div>}
      {c.status === 'signed' && <div className="alert alert--ok" role="status">Der Vertrag ist am {when(c.signedAt)} von beiden Seiten digital e-signiert. Das PDF mit dem Protokoll liegt bereit.</div>}

      {filed && <div className="alert alert--ok" role="status">{filed}</div>}
      {sent && (
        <div className="card">
          <h2>{sent.mailSent ? 'Vertrag gesendet' : 'Vertrag gesendet, aber die E-Mail ging nicht hinaus'}</h2>
          <p className="muted small">
            {sent.mailSent ? `Eine E-Mail mit dem PDF und dem Link ging an ${c.partyEmail}.` : 'Es ist kein E-Mail-Versand eingerichtet. Geben Sie den Link selbst an den Vertragspartner weiter.'} Der Link ist nur jetzt sichtbar, gespeichert ist
            nur seine Prüfsumme. Mit „Erneut senden“ erzeugen Sie später einen neuen.
          </p>
          <div className="contract-link">
            <input className="input" readOnly value={sent.link} onFocus={(e) => e.currentTarget.select()} aria-label="Link zur Unterschrift" />
            <button className="btn btn--secondary" type="button" onClick={() => void navigator.clipboard?.writeText(sent.link)}>Link kopieren</button>
          </div>
        </div>
      )}

      <div className="form-actions">
        {c.status === 'draft' && can('contracts.write') && <Link className="btn btn--secondary" to={`/vertraege/${c.id}/bearbeiten`}>Bearbeiten</Link>}
        {c.status === 'draft' && can('contracts.send') && <button className="btn btn--primary" type="button" disabled={busy} onClick={() => { setSending(true); setError(null); }}>Unterschreiben und senden</button>}
        {c.status === 'sent' && can('contracts.send') && <button className="btn btn--secondary" type="button" disabled={busy} onClick={() => { setError(null); remind.mutate(); }}>Erneut senden</button>}
        {c.status === 'sent' && can('contracts.send') && <button className="btn btn--ghost" type="button" disabled={busy} onClick={() => setVoiding(true)}>Zurückziehen</button>}
        <a className="btn btn--secondary" href={`/api/v1/contracts/${c.id}/pdf`} target="_blank" rel="noreferrer">{c.hasSignedPdf ? 'Unterschriebenes PDF' : 'PDF ansehen'}</a>
        {c.status === 'signed' && can('contracts.write') && !c.terminations.some((t) => t.status === 'draft' || t.status === 'issued' || t.status === 'delivered') && <Link className="btn btn--secondary" to={`/vertraege/${c.id}/kuendigung/neu`}>Kündigen</Link>}
        {c.status === 'signed' && can('contracts.write') && <button className="btn btn--ghost" type="button" disabled={fileDocs.isPending} onClick={() => { setError(null); fileDocs.mutate(); }}>In Dokumente ablegen</button>}
        {c.status !== 'draft' && <button className="btn btn--ghost" type="button" disabled={verify.isPending} onClick={() => { setError(null); verify.mutate(); }}>Nachweis prüfen</button>}
        {c.status === 'draft' && can('contracts.delete') && (
          <button className="btn btn--danger btn--sm" type="button" disabled={busy} onClick={() => { if (window.confirm('Entwurf endgültig löschen?')) remove.mutate(); }}>Entwurf löschen</button>
        )}
      </div>

      {sending && (
        <form className="card narrow" onSubmit={submitSend} noValidate>
          <h2>Unterschreiben und senden</h2>
          <p className="muted small">
            Mit Ihrem Passwort unterschreiben Sie den Vertrag für Ihr Unternehmen. Danach lässt er sich nicht mehr ändern, und {c.partyName} bekommt das PDF und den Link zur Unterschrift. Prüfen Sie Text und E-Mail-Adresse vorher.
          </p>
          <label className="field">
            <span>Ihr Passwort</span>
            <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={!!passwordError} />
            {passwordError && <span className="field__error" role="alert">{passwordError}</span>}
          </label>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={send.isPending || password.length === 0}>{send.isPending ? 'Wird gesendet …' : 'Unterschreiben und senden'}</button>
            <button className="btn btn--secondary" type="button" disabled={send.isPending} onClick={() => setSending(false)}>Abbrechen</button>
          </div>
        </form>
      )}

      {voiding && (
        <div className="card narrow">
          <h2>Vertrag zurückziehen</h2>
          <p className="muted small">Der Link funktioniert danach nicht mehr. Der Vertrag bleibt mit seinem Verlauf gespeichert.</p>
          <label className="field">
            <span>Grund (freiwillig)</span>
            <input className="input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="form-actions">
            <button className="btn btn--danger" type="button" disabled={voidIt.isPending} onClick={() => voidIt.mutate()}>Zurückziehen</button>
            <button className="btn btn--secondary" type="button" onClick={() => setVoiding(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {verification && (
        <div className={`alert ${verification.documentIntact && verification.eventsIntact && verification.pdfIntact !== false ? 'alert--ok' : 'alert--error'}`} role="status">
          <strong>Nachweis geprüft am {when(verification.checkedAt)}.</strong>
          <ul className="small">
            <li>Vertragstext: {verification.documentIntact ? 'unverändert seit dem Senden' : 'VERÄNDERT'}</li>
            <li>Unterschriebenes PDF: {verification.pdfIntact === null ? 'noch nicht vorhanden' : verification.pdfIntact ? 'unverändert' : 'VERÄNDERT oder nicht lesbar'}</li>
            <li>Verlauf: {verification.eventsIntact ? 'lückenlos und unverändert' : 'LÜCKE oder Änderung entdeckt'}</li>
          </ul>
        </div>
      )}

      <div className="contract-grid contract-grid--2">
        <section className="card">
          <h2>Vertragstext</h2>
          <pre className="contract-body contract-body--box">{c.body}</pre>
        </section>
        <div className="contract-grid">
          <section className="card">
            <h2>Unterschriften</h2>
            {c.signatures.length === 0 && <p className="muted">Noch keine. Das Unternehmen unterschreibt beim Senden, der Vertragspartner danach über den Link.</p>}
            {c.signatures.map((s) => (
              <p key={s.role}>
                <strong>{s.role === 'company' ? 'Unternehmen' : 'Vertragspartner'}:</strong> {s.name}
                <br />
                <span className="muted small">{when(s.signedAt)} · {s.method === 'password' ? 'Passwort' : 'Code per E-Mail'}{s.ip ? ` · IP ${s.ip}` : ''}</span>
              </p>
            ))}
            {c.documentHash && (
              <>
                <h3>Prüfsumme des Texts</h3>
                <p className="contract-hash">{c.documentHash}</p>
              </>
            )}
            {c.signedPdfHash && (
              <>
                <h3>Prüfsumme des unterschriebenen PDFs</h3>
                <p className="contract-hash">{c.signedPdfHash}</p>
              </>
            )}
          </section>
          {(c.status === 'signed' || c.terminations.length > 0) && (
            <section className="card">
              <h2>Kündigung</h2>
              {c.terminations.length === 0 && <p className="muted">Keine. Mit „Kündigen“ erfassen Sie eine Kündigung des Unternehmens oder den Eingang einer Kündigung des Vertragspartners.</p>}
              <ul className="contract-timeline">
                {c.terminations.map((t) => (
                  <li key={t.id}>
                    <strong><Link to={`/vertraege/${c.id}/kuendigung/${t.id}`} className="strong-link">{t.number}</Link> · {TERMINATION_STATUS_LABELS[t.status]}</strong>
                    <time>{t.initiatedBy === 'company' ? 'Vom Unternehmen' : `Von ${c.partyName}`}{t.status === 'delivered' && t.effectiveDate ? ` · Vertrag endet ${new Intl.DateTimeFormat('de-DE', { timeZone: 'UTC' }).format(new Date(`${t.effectiveDate}T12:00:00Z`))}` : ''}</time>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="card">
            <h2>Verlauf</h2>
            {c.events.length === 0 && <p className="muted">Der Verlauf beginnt mit dem Senden.</p>}
            <ol className="contract-timeline">
              {c.events.map((e) => (
                <li key={e.id}>
                  <strong>{CONTRACT_EVENT_LABELS[e.kind] ?? e.kind}</strong>
                  <time dateTime={e.at}>{when(e.at)}{e.actor ? ` · ${e.actor}` : ''}{e.ip ? ` · IP ${e.ip}` : ''}</time>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </>
  );
}
